import React, { useState, useMemo, useCallback } from 'react';
import { Grid, Row, Col, Text, Button } from 'rsuite';
import {
  STRIPE_NO_PAYMENT_METHOD_ATTACHED_INTENT_STATUSES,
  SUCCESSFUL_STRIPE_ACH_PAYMENT_INTENT_STATUSES,
} from './stripeConstants';
import { CANCELLED_ALERT_MSG, DEFAULT_ALERT_MSG } from '../constants';
import { useSelector } from 'react-redux';
import {
  selectPaymentFees,
  selectSelectedPaymentMethod,
} from '../../../store/slices/payment/paymentSelectors';
import { selectConfigExpressSettingsName } from '../../../store/slices/config/configSelectors';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import { convertNumberToCurrency } from '../../../common/utils';
import { calculateTotalDue } from '../common/utils';
import Notice from '../../user-feedback/Notice';

const StripeACHPaymentConfirmationForm = ({
  stripe,
  bankAccountDetails,
  setBankAccountDetails,
  clientSecret,
  setClientSecret,
  onSuccess,
}) => {
  const [paymentInProgress, setPaymentsInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const payButtonDisabled = useMemo(
    () => paymentInProgress || !stripe,
    [paymentInProgress, stripe]
  );

  const billingEntityName = useSelector(selectConfigExpressSettingsName);
  const paymentFees = useSelector(selectPaymentFees);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);

  const totalDue = useMemo(
    () => calculateTotalDue(selectedPaymentMethod, paymentFees),
    [selectedPaymentMethod, paymentFees]
  );

  const bankAccountDetailsToDisplay = [
    { label: 'Date', value: dayjs().format('MMMM DD, YYYY') },
    { label: "Account Holder's Name", value: bankAccountDetails.accountHolder },
    { label: 'Bank Name', value: bankAccountDetails.bankName },
    { label: 'Routing Number', value: bankAccountDetails.routingNumber },
    { label: 'Account Number', value: `****${bankAccountDetails.last4}` },
    {
      label: 'Account Type',
      value: capitalize(bankAccountDetails.accountType),
    },
  ];

  const payDisclaimerText = useMemo(
    () =>
      `By clicking "Pay", you authorize ${billingEntityName} to debit the bank account specified above
            for any amount owed for charges arising from your registration, renewal and/or transient occupancy tax
            pursuant to applicable ordinances and by-laws of ${billingEntityName}
            until this authorization is revoked.
            You may amend or cancel this authorization at any time by providing
            ${billingEntityName} with a 30 (thirty) days notice.`,

    [billingEntityName]
  );

  const resetPaymentIntent = useCallback(() => {
    setClientSecret();
    setBankAccountDetails({});
  }, [setBankAccountDetails, setClientSecret]);

  const processPayment = async () => {
    setErrorMessage('');
    if (!stripe) return;
    setPaymentsInProgress(true);
    const { paymentIntent, error } =
      await stripe.confirmUsBankAccountPayment(clientSecret);

    if (error) {
      setErrorMessage(DEFAULT_ALERT_MSG);
    } else if (
      STRIPE_NO_PAYMENT_METHOD_ATTACHED_INTENT_STATUSES.includes(
        paymentIntent.status
      )
    ) {
      setErrorMessage(CANCELLED_ALERT_MSG);
    } else if (
      SUCCESSFUL_STRIPE_ACH_PAYMENT_INTENT_STATUSES.includes(
        paymentIntent.status
      )
    ) {
      onSuccess();
    } else {
      setErrorMessage(DEFAULT_ALERT_MSG);
    }
    setPaymentsInProgress(false);
  };
  return (
    <>
      <Grid fluid>
        <Row className="mb-16">
          <Col xs={24}>
            <Text>Please confirm your bank account details below.</Text>
          </Col>
        </Row>
        <Row className="mb-24">
          <Col xs={24}>
            <Button
              className="btn-link"
              onClick={() => {
                resetPaymentIntent();
              }}
            >
              Use a different account instead
            </Button>
          </Col>
        </Row>
        {bankAccountDetailsToDisplay.map(({ label, value }) => (
          <Row
            className="mb-8"
            key={label}
          >
            <Col
              key={label}
              xs={12}
              md={8}
            >
              <Text className="text-small text-bold">{label}:</Text>
            </Col>
            <Col
              key={label}
              xs={12}
              md={16}
            >
              <Text className="text-small">{value}</Text>
            </Col>
          </Row>
        ))}
        <Row className="mb-24 mt-24 pt-16">
          <Col xs={24}>
            <Text className="text-small">{payDisclaimerText}</Text>
          </Col>
        </Row>
        {errorMessage && (
          <Row className="mb-16">
            <Col xs={24}>
              <Notice message={errorMessage} />
            </Col>
          </Row>
        )}
        <Row className="pt-16">
          <Col xs={24}>
            <Button
              className="btn-primary full-width"
              disabled={payButtonDisabled}
              onClick={() => {
                processPayment();
              }}
            >
              Pay {convertNumberToCurrency(totalDue)}
            </Button>
          </Col>
        </Row>
      </Grid>
    </>
  );
};

export default StripeACHPaymentConfirmationForm;
